import React, { useEffect } from 'react'
import { observer } from 'mobx-react'

import { useHistory, Link } from 'react-router-dom'
import { Alert, FormGroup, FormFeedback, Input, Button, Label } from 'reactstrap'
import { Form, Field } from 'react-final-form'
import { FORM_ERROR } from 'final-form'
import { ErrorAlert, Loading } from '../../components'
import { AuthPage } from './AuthPage'

import './Confirm.css'
import { useEmailConfirmationCode, useQualifications, useQueryParams } from '../../hooks'
import { useStores } from '../../stores/RootStore'
import { launchContact } from '../../components/utils'

const debug = require('debug')('sd:Confirm')

const _Confirm = () => {
  const qParams = useQueryParams()
  const { code, registration } = qParams
  const { stores } = useStores()
  const { auth } = stores || {}
  const { cognitoUser, user, emailForLogin } = auth || {}
  const { primaryEmail } = user || {}
  const { storedValue } = useQualifications()
  const { email: emailStored } = storedValue
  const confirmEmail = emailForLogin || primaryEmail || emailStored
  const history = useHistory()
  const {
    resendConfirmationCode,
    resending,
    resendError,
    success
  } = useEmailConfirmationCode()

  const loginRedirect = () => {
    history.push('/login?confirm-success=true')
  }

  const handleOnSubmit = async (values) => {
    const { username, code } = values
    try {
      await auth.confirmRegistration(code, username)
      const form = document.getElementById('confirmation-code-form')
      if (window.analytics) {
        const currentEvent = 'Registration: confirm user'
        const currentHistory = window?.analyticsEventHistory
        const newHistory = [currentEvent, ...currentHistory].slice(0, 10)
        window.analyticsEventHistory = newHistory
        const fullFilled = {
          action: 'User clicks confirm',
          pathHistory: window?.analyticsPathHistory,
          priorPath: window?.analyticsPathHistory?.[0],
          eventHistory: newHistory,
          priorEvent: newHistory?.[1]
        }
        window.analytics.trackForm(form, currentEvent, fullFilled)
      }
      return loginRedirect()
    } catch (err) {
      if (err.message === 'User cannot be confirmed. Current status is CONFIRMED') {
        return loginRedirect()
      } else {
        return { [FORM_ERROR]: err.message }
      }
    }
  }

  useEffect(() => {
    if (code && cognitoUser) handleOnSubmit({ code })
    // eslint-disable-next-line
  }, [])

  return (
    <AuthPage
      headerSubtitle={
        <p className='d-flex justify-content-center align-items-baseline'>
          Back to <Button className='pl-1' color='link' to='/login' tag={Link}>Login</Button>
        </p>
      }
      form={
        <Form
          onSubmit={handleOnSubmit}
          initialValues={{ code }}
          render={state => {
            debug('FinalForm.render(state)', state)
            const { handleSubmit, submitError, hasValidationErrors, form, submitting, pristine, values } = state
            return (
              <>
                <form className='auth-form' id='confirmation-code-form' onSubmit={handleSubmit}>
                  {debug('FinalForm.render', { handleSubmit, submitError, form, submitting, pristine, values })}
                  {registration
                    ? (
                      <Alert color='success'>
                        <h3>Success!</h3>
                        <p className='mb-0'>
                          A confirmation email has been sent to {confirmEmail ? <b>{confirmEmail}</b> : 'the email address you provided'}.
                        </p>
                      </Alert>)
                    : <p>Please check your email for your confirmation code and enter it here.</p>}
                  {success
                    ? (
                      <Alert color='success'>
                        Code sent to {confirmEmail ? <b>{confirmEmail}</b> : 'the email address you provided'}. Please check your email.
                      </Alert>)
                    : null}

                  {/* If we have a user loaded into our state, we don't need to gather their email. */
                    cognitoUser
                      ? null
                      : (
                        <FormGroup>
                          <div className='form-label-group'>
                            <Field
                              name='username'
                              validate={username => {
                                if (!username) return 'Username is Required'
                              }}
                            >
                              {({ input, meta }) => (
                                <>
                                  <Input
                                    {...input}
                                    autoFocus
                                    type='username'
                                    valid={meta.valid}
                                    invalid={meta.error && meta.touched && !meta.pristine}
                                    placeholder='username'
                                  />
                                  <Label for='username'>Username</Label>
                                  {meta.error && meta.touched && !meta.pristine && <FormFeedback>{meta.error}</FormFeedback>}
                                </>
                              )}
                            </Field>
                          </div>
                        </FormGroup>)
                  }

                  <FormGroup>
                    <div className='form-label-group'>
                      <Field
                        name='code'
                        validate={code => code ? undefined : 'Confirmation code is required'}
                      >
                        {({ input, meta }) => (
                          <>
                            <Input
                              {...input}
                              autoFocus
                              type='text'
                              valid={meta.valid}
                              invalid={meta.error && meta.touched && !meta.pristine}
                              placeholder='Confirmation code'
                            />
                            <Label for='code'>Confirmation code</Label>
                            {meta.error && meta.touched && !meta.pristine && <FormFeedback>{meta.error}</FormFeedback>}
                          </>
                        )}
                      </Field>
                    </div>
                  </FormGroup>

                  <FormGroup>
                    {(submitError || resendError) &&
                      <ErrorAlert error={submitError || resendError} />}
                    {submitting ? <Loading /> : null}
                    <Button type='submit' color='primary' disabled={submitting || pristine || hasValidationErrors || resending} block>
                      Confirm
                    </Button>

                    {
                      !cognitoUser
                        ? null
                        : (
                          <Button
                            type='submit'
                            color='link'
                            className='pl-0 mt-2'
                            disabled={submitting || resending}
                            onClick={async event => {
                              event.preventDefault()
                              await resendConfirmationCode(values.email)
                            }}
                          >
                            Resend Confirmation Code
                          </Button>)
                    }
                    <p className='align-items-center mb-0 mt-4'>
                      Questions? <Button color='link' className='p-0 mt-n1' onClick={launchContact}>Click here</Button> to tell us your needs and we'll help you.
                    </p>
                  </FormGroup>
                </form>
              </>
            )
          }}
        />
      }
    />
  )
}

const Confirm = observer(_Confirm)

export default Confirm

debug('loaded')
